<ng-template #cardLogo>
  @if (!cardType()) {
    <img class="h-6 w-6" src="/images/default-card.png" alt="Card" />
  } @else if (cardType() === 'Visa') {
    <img src="/images/visa_icon.svg" alt="Visa" class="h-6 w-6" />
  } @else if (cardType() === 'MasterCard') {
    <img src="/images/mastercard_icon.svg" alt="MasterCard" class="h-6 w-6" />
  }
</ng-template>

<ng-template #cardErrorTpl let-control>
  @if (control.errors?.['required']) {
    Card number is required.
  } @else if (control.errors?.['pattern']) {
    Enter a valid Visa or Mastercard number.
  }
</ng-template>

<ng-template #expiryErrorTpl let-control>
  @if (control.errors?.['required']) {
    Expiry date is required.
  } @else if (control.errors?.['expired']) {
    This card has expired.
  } @else if (control.errors?.['pattern'] || control.errors?.['minlength']) {
    Enter a valid expiry date (MM/YY).
  }
</ng-template>

<ng-template #cvvErrorTpl let-control>
  @if (control.errors?.['required']) {
    CVV is required.
  } @else {
    Enter a valid CVV.
  }
</ng-template>

@if (compact()) {
  <!-- Compact, labelled card panel (checkout dialog) -->
  <form nz-form nzLayout="vertical" [formGroup]="paymentForm" class="mt-4">
    <div class="flex flex-col border border-gray-300 rounded-lg py-4 px-6 bg-[#f3e9eb]">
      <nz-form-item>
        <nz-form-label class="!text-main-color !font-normal">Card Number</nz-form-label>
        <nz-form-control [nzErrorTip]="cardErrorTpl">
          <nz-input-wrapper class="!bg-transparent">
            <span nzInputPrefix>
              <ng-container *ngTemplateOutlet="cardLogo"></ng-container>
            </span>
            <input
              nz-input nzSize="large"
              type="text"
              maxlength="19"
              formControlName="cardNumber"
              libCardNumberFormat
              placeholder="Enter card number"
              (cardTypeDetected)="setCardType($event)"
            />
          </nz-input-wrapper>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label class="!text-main-color !font-normal">Expiry Date</nz-form-label>
        <nz-form-control [nzErrorTip]="expiryErrorTpl">
          <input
            #expiryDateInput
            nz-input nzSize="large"
            class="!bg-transparent"
            type="text"
            maxlength="5"
            formControlName="expiryDate"
            libExpiryFormat
            placeholder="Enter expiry date (MM/YY)"
          />
        </nz-form-control>
      </nz-form-item>

      <nz-form-item class="!mb-0">
        <nz-form-label class="!text-main-color !font-normal">CVV</nz-form-label>
        <nz-form-control [nzErrorTip]="cvvErrorTpl">
          <input
            #cvvInput
            nz-input nzSize="large"
            class="!bg-transparent"
            type="text"
            maxlength="4"
            formControlName="cvv"
            libNumericOnly
            placeholder="Enter card cvv code"
          />
        </nz-form-control>
      </nz-form-item>
    </div>
  </form>
} @else {
  <!-- Inline card row (full-page checkout) -->
  <form nz-form nzLayout="vertical" [formGroup]="paymentForm" class="mt-4">
    <nz-form-item>
      <nz-form-control [nzErrorTip]="cardErrorTpl">
        <nz-input-wrapper>
          <span nzInputPrefix>
            <ng-container *ngTemplateOutlet="cardLogo"></ng-container>
          </span>
          <input
            nz-input nzSize="large"
            type="text"
            maxlength="19"
            formControlName="cardNumber"
            libCardNumberFormat
            placeholder="Card number"
            (cardTypeDetected)="setCardType($event)"
          />
        </nz-input-wrapper>
      </nz-form-control>
    </nz-form-item>

    <div class="flex gap-4">
      <nz-form-item class="flex-1">
        <nz-form-control [nzErrorTip]="expiryErrorTpl">
          <input
            #expiryDateInput
            nz-input nzSize="large"
            type="text"
            maxlength="5"
            formControlName="expiryDate"
            libExpiryFormat
            placeholder="MM/YY"
          />
        </nz-form-control>
      </nz-form-item>

      <nz-form-item class="flex-1">
        <nz-form-control [nzErrorTip]="cvvErrorTpl">
          <input
            #cvvInput
            nz-input nzSize="large"
            type="text"
            maxlength="4"
            formControlName="cvv"
            libNumericOnly
            placeholder="CVV"
          />
        </nz-form-control>
      </nz-form-item>
    </div>
  </form>
}

<!-- Hidden form posted into the 3-D Secure iframe -->
<form
  #secureForm
  method="POST"
  [action]="payloadConfig().gateway"
  enctype="multipart/form-data"
  target="3dsecureIframe"
  class="hidden"
>
  <input type="hidden" name="ApplicationID" [value]="payloadConfig().ApplicationID" />
  <input type="hidden" name="ReturnUrl" [value]="payloadConfig().ReturnUrl" />
  <input type="hidden" name="MerchantReference" [value]="payloadConfig().MerchantReference || referenceNumber()" />
  <input type="hidden" name="Amount" [value]="payloadConfig().Amount" />
  <input type="hidden" name="Currency" [value]="payloadConfig().Currency" />
  <input type="hidden" name="PAN" [value]="paymentForm.get('cardNumber')?.value" />
  <input type="hidden" name="ExpiryDate" [value]="validExpiryDate" />
  <input type="hidden" name="CardSecurityCode" [value]="paymentForm.get('cvv')?.value" />
</form>

<div class="flex flex-col gap-4" [ngClass]="compact() ? 'pt-3' : 'pt-4'">
  @if (!compact()) {
    <p class="leading-normal text-xs">
      By paying this fee and clicking on "Pay", you agree to the
      <a class="cursor-pointer underline" target="_blank" rel="noopener noreferrer" href="">terms of service</a>
      and
      <a class="cursor-pointer underline" target="_blank" rel="noopener noreferrer" href="">Privacy policy</a>
    </p>
  }

  <button
    nz-button nzSize="large"
    nzType="primary"
    nzBlock
    type="button"
    [nzLoading]="isProcessing()"
    [disabled]="
      isProcessing() ||
      (payloadConfig().Amount ?? 0) > 100000000 ||
      (payloadConfig().Amount ?? 0) < 1 ||
      paymentForm.invalid
    "
    (click)="onPay(payloadConfig().feeId)"
  >
    {{ isProcessing() ? 'Processing...' : 'Pay ' + payloadConfig().Currency + ' ' + payloadConfig().Amount }}
  </button>

  @if (!compact()) {
    <p class="leading-normal text-xs opacity-75">
      Payments are processed in USD. Payment provider fees may apply.
    </p>
  }
</div>

<!-- 3-D Secure step (html-driven nz-modal) -->
<nz-modal
  [nzVisible]="show3dSecure()"
  nzTitle="Secure payment"
  [nzFooter]="null"
  [nzWidth]="520"
  [nzMaskClosable]="false"
  (nzOnCancel)="show3dSecure.set(false)"
>
  <ng-container *nzModalContent>
    @if (tempLoading()) {
      <div class="flex flex-col justify-center items-center h-[400px]">
        <nz-spin nzSimple nzSize="large"></nz-spin>
        <p class="mt-6 text-gray-500">Please wait...</p>
      </div>
    }
    <iframe
      sandbox="allow-scripts allow-top-navigation allow-top-navigation-by-user-activation allow-forms allow-popups allow-same-origin"
      #secureIframe
      name="3dsecureIframe"
      title="3D Secure Payment"
      [style.display]="tempLoading() ? 'none' : 'block'"
      style="width: 100%; height: 400px; border: 0"
    ></iframe>
  </ng-container>
</nz-modal>
